import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Save, Search, Wrench, RotateCcw } from 'lucide-react';
import { HELIX_DATA, HELIX_GROUPS } from './HelixData';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';
import ReplaceableImage from './Configure/components/ReplaceableImage';

// Bump when the default HTML layout changes — un-edited items regenerate once.
const HTML_VERSION = 1;
const TOTAL        = HELIX_DATA.length;
const ALL          = 'All Categories';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/* ── Default HTML for an item (same heading style as Vastu Devta Guide) ── */
export const buildInitialHtml = (d) => {
  const p = [];

  if (d.group === 'Metal Helix' || d.group === 'Helix Frame') {
    if (d.zone)    p.push(`<h2>Zone</h2><p>${esc(d.zone)}</p>`);
    if (d.element) p.push(`<h2>Element</h2><p>${esc(d.element)}</p>`);
    if (d.intro)   p.push(`<p>${esc(d.intro)}</p>`);
    if (d.defects?.length) {
      p.push('<h1>BEST REMEDY FOR DEFECTS LIKE</h1>');
      p.push('<ul>' + d.defects.map(x => `<li>${esc(x)}</li>`).join('') + '</ul>');
    }
    if (d.install) { p.push('<h1>INSTALLATION</h1>'); p.push(`<p>${esc(d.install)}</p>`); }
    return p.join('') || '<p><br></p>';
  }

  // Springs & Studs
  if (d.element) p.push(`<h2>Element</h2><p>${esc(d.element)}</p>`);
  if (d.intro)   p.push(`<p>${esc(d.intro)}</p>`);
  if (d.use)     { p.push('<h1>USE &amp; PLACEMENT</h1>'); p.push(`<p>${esc(d.use)}</p>`); }
  return p.join('') || '<p><br></p>';
};

export const isStillDefaultHtml = (d, html) => !html || buildInitialHtml(d) === html;

const migrate = (d) => {
  if (d.richHtml !== undefined && d._htmlVersion === HTML_VERSION) return d;
  // Never overwrite a hand-edited body just because the layout version moved.
  if (d.richHtml !== undefined && d._htmlVersion !== undefined && !isStillDefaultHtml(d, d.richHtml)) {
    return { ...d, _htmlVersion: HTML_VERSION };
  }
  return { ...d, richHtml: buildInitialHtml(d), _htmlVersion: HTML_VERSION };
};

/* ── Toggle ──────────────────────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}>
    <input type="checkbox" checked={checked} onChange={onChange} style={{ opacity:0, width:0, height:0 }} />
    <span style={{ position:'absolute', inset:0, background:checked?'#00A19D':'#CBD5E1', borderRadius:24, transition:'background .2s' }} />
    <span style={{ position:'absolute', top:2, left:checked?22:2, width:20, height:20, background:'#fff', borderRadius:'50%', transition:'left .2s', boxShadow:'0 1px 4px rgba(0,0,0,.18)' }} />
  </label>
);

/* ── Rich Text Editor ────────────────────────────────────────────── */
const RichEditor = React.memo(function RichEditor({ value, onChange, itemId }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '<p><br></p>';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => { onChange(editorRef.current?.innerHTML || ''); }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e8eaed', borderRadius: 4, background: '#fff', color: '#3c4043', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', height: 26, padding: '0 7px',
    fontSize: '0.74rem', fontFamily: 'inherit', ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display:'flex', alignItems:'center', gap:3, padding:'5px 8px', background:'#fafafa', borderBottom:'1px solid #e8eaed', flexWrap:'wrap' }}>
        <select
          onMouseDown={e => e.preventDefault()}
          onChange={e => { formatBlock(e.target.value); e.target.value = ''; }}
          defaultValue=""
          style={{ fontSize:'0.71rem', fontWeight:600, border:'1px solid #e8eaed', borderRadius:4, padding:'2px 6px', color:'#3c4043', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit', height:26 }}
        >
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h1">H1 — Main Heading</option>
          <option value="h2">H2 — Sub Heading</option>
          <option value="h3">H3 — Title</option>
        </select>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('bold');}}      style={btn({fontWeight:800,width:26,padding:0})}>B</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('italic');}}    style={btn({fontStyle:'italic',width:26,padding:0})}>I</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('underline');}} style={btn({textDecoration:'underline',width:26,padding:0})}>U</button>
        <span style={{ width:1, height:16, background:'#e8eaed', margin:'0 2px' }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertOrderedList');}}   style={btn()}>1. List</button>
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('insertUnorderedList');}} style={btn()}>• List</button>
        <span style={{ flex:1 }} />
        <button type="button" onMouseDown={e=>{e.preventDefault();exec('removeFormat');}} style={btn({color:'#80868b',fontSize:'0.67rem'})}>Clear</button>
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder="Type here… use toolbar to format"
        className="hlx-editor"
        style={{ minHeight:140, padding:'12px 14px', outline:'none', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", fontSize:'0.84rem', color:'#202124', lineHeight:1.65 }}
      />
    </div>
  );
});

/* ── Item card: number + name + toggle, image(s), rich-text editor ── */
const HelixCard = React.memo(function HelixCard({ item, resetCount, onToggle, onHtmlChange, onReset, onImageChange }) {
  const isEdited = !isStillDefaultHtml(item, item.richHtml);
  return (
    <div style={{
      background:'#fff', borderRadius:12,
      border:`1px solid ${item.enabled ? '#00A19D' : '#e2e8f0'}`,
      boxShadow: item.enabled ? '0 1px 12px rgba(0,161,157,.08)' : '0 1px 3px rgba(0,0,0,.04)',
      overflow:'hidden', transition:'border-color .2s',
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 14px', background: item.enabled ? '#f0fdfc' : '#fafafa', borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ width:30, height:30, borderRadius:7, flexShrink:0, background:item.enabled?'#00A19D':'#e2e8f0', color:item.enabled?'#fff':'#94a3b8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.76rem', fontWeight:700 }}>
          {item.id}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'0.88rem', fontWeight:700, color:item.enabled?'#0f172a':'#94a3b8', letterSpacing:'-0.01em', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
          <div style={{ fontSize:'0.66rem', color:'#94a3b8', fontWeight:600 }}>{item.group}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:7, flexShrink:0 }}>
          {isEdited && (
            <>
              <span title="Edited — differs from the default text" style={{ fontSize:'0.62rem', color:'#b45309', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'1px 6px', fontWeight:700 }}>Edited</span>
              <button type="button" onClick={onReset} title="Reset to default text"
                style={{ border:'1px solid #e2e8f0', background:'#fff', color:'#64748b', borderRadius:5, width:24, height:24, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>
                <RotateCcw size={12} />
              </button>
            </>
          )}
          <span style={{ fontSize:'0.68rem', color:item.enabled?'#00857E':'#94a3b8', fontWeight:600 }}>{item.enabled ? 'ON' : 'OFF'}</span>
          <Toggle checked={item.enabled} onChange={onToggle} />
        </div>
      </div>

      {item.images?.length > 0 && (
        <div style={{ background:'#f8fafc', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'center', gap:10, padding:'10px 14px', flexWrap:'wrap' }}>
          {item.images.map((src, i) => (
            <ReplaceableImage key={src}
              src={(item.customImages && item.customImages[i]) || src}
              isCustom={!!(item.customImages && item.customImages[i])}
              alt={item.name}
              imgStyle={{ maxHeight:170 }}
              wrapStyle={{ maxWidth:'48%' }}
              onReplace={url => onImageChange(i, url)}
              onReset={() => onImageChange(i, null)} />
          ))}
        </div>
      )}

      <div style={{ padding:'12px 14px 14px' }}>
        <RichEditor itemId={`${item.id}-${resetCount}`} value={item.richHtml} onChange={onHtmlChange} />
      </div>
    </div>
  );
});

/* ── Main Component — fully controlled by openModal prop ─────────── */
export default function Helix({ openModal = false, onModalClose = null, onReportGenerated = null, projectId = null }) {
  const isMobile = useIsMobile();
  const STORAGE_KEY = `helix_data_${projectId || 'new'}`;
  const [items, setItems]       = useState([]);
  const [resets, setResets]     = useState({});
  const [search, setSearch]     = useState('');
  const [group, setGroup]       = useState(ALL);
  const [isSaving, setIsSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const scrollRef = useRef(null);

  const closeModal = () => { if (onModalClose) onModalClose(); };

  // Saved data is slim ({ id, enabled, richHtml }) — names/images/defaults
  // always come from HELIX_DATA, so the project payload stays small.
  useEffect(() => {
    let parsed = [];
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      parsed = saved ? JSON.parse(saved) : [];
    } catch { parsed = []; }
    setItems(HELIX_DATA.map(d => {
      const ov = Array.isArray(parsed) ? parsed.find(p => p.id === d.id) : null;
      return migrate(ov ? { ...d, ...ov } : { ...d, enabled: false });
    }));
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const inGroup = useCallback((d) => group === ALL || d.group === group, [group]);

  const handleToggle    = id  => setItems(p => p.map(d => d.id === id ? { ...d, enabled: !d.enabled } : d));
  const handleToggleAll = val => setItems(p => p.map(d => inGroup(d) ? { ...d, enabled: val } : d));
  const handleHtml      = useCallback((id, html) => setItems(p => p.map(d => d.id === id ? { ...d, richHtml: html } : d)), []);
  // Replace / reset ONE of an item's pictures (customImages = { [pictureIndex]: user's own picture })
  const handleImage     = useCallback((id, index, url) => setItems(p => p.map(d => {
    if (d.id !== id) return d;
    const next = { ...(d.customImages || {}) };
    if (url) next[index] = url; else delete next[index];
    return { ...d, customImages: Object.keys(next).length ? next : undefined };
  })), []);
  const handleReset     = (id) => {
    setItems(p => p.map(d => d.id === id ? { ...d, richHtml: buildInitialHtml(d) } : d));
    setResets(r => ({ ...r, [id]: (r[id] || 0) + 1 }));
  };

  const handleSave = () => {
    setIsSaving(true);
    try {
      const slim = items.map(({ id, enabled, richHtml, _htmlVersion, customImages }) => ({ id, enabled, richHtml, _htmlVersion, ...(customImages ? { customImages } : {}) }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slim));
      if (onReportGenerated) onReportGenerated({ reportData: slim });
      setSavedMsg('All changes saved');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch { setSavedMsg('Save failed'); }
    setIsSaving(false);
  };

  const filtered = useMemo(() => items.filter(d => {
    if (!inGroup(d)) return false;
    return !search || d.name.toLowerCase().includes(search.toLowerCase());
  }), [items, search, inGroup]);

  const enabledCount = items.filter(d => d.enabled).length;
  const scoped       = items.filter(inGroup);

  const leave = useLeaveConfirm({ onSave: () => { handleSave(); closeModal(); }, onLeave: closeModal });

  if (!openModal) return null;

  const allEnabled  = scoped.length > 0 && scoped.every(d => d.enabled);
  const allDisabled = scoped.length > 0 && scoped.every(d => !d.enabled);

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10500, display:'flex', flexDirection:'column', background:'#f1f5f9', fontFamily:"'Inter','Segoe UI',system-ui,sans-serif", textAlign:'left' }}>

      <style>{`
        .hlx-editor h1 { font-size:1.0rem; font-weight:800; color:#0f172a; margin:14px 0 2px; line-height:1.3; border-bottom:1.5px solid #e2e8f0; padding-bottom:4px; }
        .hlx-editor h1:first-child { margin-top:0; }
        .hlx-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:8px 0 1px; line-height:1.4; }
        .hlx-editor h3 { font-size:0.8rem; font-weight:600; color:#334155; margin:6px 0 1px; line-height:1.4; }
        .hlx-editor p  { font-size:0.82rem; color:#475569; margin:1px 0 5px; line-height:1.65; }
        .hlx-editor ul, .hlx-editor ol { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
        .hlx-editor li { margin-bottom:2px; }
        .hlx-editor strong { font-weight:700; color:#1e293b; }
        .hlx-editor em { font-style:italic; }
        .hlx-editor:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
        .hlx-editor:focus { outline:none; }
      `}</style>

      {/* ── Header ── */}
      <div style={{ background:'#0f172a', color:'#fff', padding: isMobile ? '0 14px' : '0 24px', display:'flex', alignItems:'center', justifyContent:'space-between', height:56, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 10 : 14, minWidth:0 }}>
          <div style={{ width:32, height:32, borderRadius:8, background:'linear-gradient(135deg,#00857E,#00C4BF)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><Wrench size={16} color="#fff" /></div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'0.94rem', fontWeight:700, lineHeight:1.2 }}>Helix, Spring and Stud</div>
            {!isMobile && <div style={{ fontSize:'0.68rem', color:'#64748b' }}>Metal Helix, Frames, Springs &amp; Studs</div>}
          </div>
          {!isMobile && (
            <>
              <div style={{ width:1, height:26, background:'rgba(255,255,255,.1)', margin:'0 4px' }} />
              <span style={{ fontSize:'0.72rem', background:'rgba(0,161,157,.2)', color:'#4ffffa', border:'1px solid rgba(0,161,157,.35)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{enabledCount} enabled</span>
              <span style={{ fontSize:'0.72rem', background:'rgba(255,255,255,.06)', color:'#94a3b8', border:'1px solid rgba(255,255,255,.1)', borderRadius:20, padding:'2px 10px', fontWeight:600 }}>{TOTAL - enabledCount} disabled</span>
            </>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap: isMobile ? 6 : 8, flexShrink:0 }}>
          {savedMsg && !isMobile && <span style={{ fontSize:'0.76rem', color:'#4ade80', fontWeight:500 }}>✓ {savedMsg}</span>}
          {isMobile ? (
            <button onClick={handleSave} disabled={isSaving} title={isSaving ? 'Saving…' : 'Save Report'}
              style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, width:34, height:34, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:isSaving?.7:1, flexShrink:0 }}>
              <Save size={14} />
            </button>
          ) : (
            <>
              <button onClick={handleSave} disabled={isSaving}
                style={{ background:'#00A19D', border:'none', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5, opacity:isSaving?.7:1 }}>
                <Save size={13} />{isSaving ? 'Saving…' : 'Save Report'}
              </button>
              <button onClick={() => { handleSave(); closeModal(); }}
                style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.12)', color:'#fff', borderRadius:7, padding:'7px 18px', fontWeight:600, fontSize:'0.8rem', cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}>
                <Save size={13} />Save &amp; Close
              </button>
            </>
          )}
          <button onClick={leave.requestClose}
            style={{ background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)', color:'#94a3b8', borderRadius:7, padding: isMobile ? '9px' : '7px 9px', cursor:'pointer', display:'flex', alignItems:'center', flexShrink:0 }}>
            <X size={15} />
          </button>
          {leave.dialog}
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #e2e8f0', padding: isMobile ? '8px 14px' : '8px 24px', display:'flex', alignItems:'center', gap:10, flexShrink:0, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, flex: isMobile ? '1 1 100%' : '0 0 auto' }}>
          <span style={{ fontSize:'0.72rem', fontWeight:600, color:'#64748b', whiteSpace:'nowrap' }}>Category</span>
          <select value={group} onChange={e => { setGroup(e.target.value); setSearch(''); }}
            style={{ fontSize:'0.78rem', fontWeight:600, border:'1.5px solid #e2e8f0', borderRadius:7, padding:'5px 10px', color:'#0f172a', background:'#fff', cursor:'pointer', outline:'none', fontFamily:'inherit', minWidth:170, flex: isMobile ? 1 : 'initial' }}>
            <option value={ALL}>{`${ALL} (${TOTAL})`}</option>
            {HELIX_GROUPS.map(g => (
              <option key={g} value={g}>{`${g} — ${HELIX_DATA.filter(d => d.group === g).length}`}</option>
            ))}
          </select>
        </div>

        {!isMobile && <div style={{ width:1, height:22, background:'#e2e8f0' }} />}

        <div style={{ position:'relative', flex: isMobile ? '1 1 100%' : '0 0 220px' }}>
          <Search size={13} style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:'#94a3b8' }} />
          <input type="text" placeholder="Search name…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ width:'100%', boxSizing:'border-box', paddingLeft:28, paddingRight:8, height:34, border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:'0.78rem', color:'#334155', outline:'none', fontFamily:'inherit', background:'#f8fafc' }} />
        </div>

        {!isMobile && <div style={{ width:1, height:22, background:'#e2e8f0' }} />}

        <button onClick={() => handleToggleAll(true)}  style={allEnabled  ? tbBtn('#00A19D','#fff') : tbBtn('#fff','#64748b','#e2e8f0')}>Enable All</button>
        <button onClick={() => handleToggleAll(false)} style={allDisabled ? tbBtn('#00A19D','#fff') : tbBtn('#fff','#64748b','#e2e8f0')}>Disable All</button>

        <div style={{ marginLeft: isMobile ? 0 : 'auto', fontSize:'0.76rem', color:'#94a3b8', fontWeight:500 }}>
          {filtered.length} of {TOTAL}{group !== ALL ? ` in ${group}` : ''}
        </div>
      </div>

      {/* ── Cards ── */}
      <div ref={scrollRef} style={{ flex:1, overflowY:'auto', padding: isMobile ? '14px 14px 30px' : '20px 24px 40px' }}>
        <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill,minmax(560px,1fr))', gap: isMobile ? 12 : 18 }}>
          {filtered.map(item => (
            <HelixCard
              key={item.id}
              item={item}
              resetCount={resets[item.id] || 0}
              onToggle={() => handleToggle(item.id)}
              onHtmlChange={html => handleHtml(item.id, html)}
              onReset={() => handleReset(item.id)}
              onImageChange={(i, url) => handleImage(item.id, i, url)}
            />
          ))}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding:'60px 20px', color:'#94a3b8', textAlign:'center' }}>
            <div style={{ fontSize:'2rem', marginBottom:10 }}>🔍</div>
            <div style={{ fontSize:'0.88rem', fontWeight:500 }}>No match for "{search}"</div>
          </div>
        )}
      </div>
    </div>
  );
}

function tbBtn(bg, color, border) {
  return { background:bg, color, border:`1.5px solid ${border||bg}`, borderRadius:7, padding:'5px 13px', fontSize:'0.76rem', fontWeight:600, cursor:'pointer' };
}
